import { useEffect, useRef, useState } from 'react';
import { Printer, Tag } from 'lucide-react';
import { api } from '../api';
import { tlacHtml } from '../lib/tlac';
import { useToast } from './ui';

/**
 * .949 Tlačová stanica štítkov.
 *
 * Oto: z telefónu (skener v sklade) „hneď si dať tlačiť štítky na tlačiareň,
 * ktorú mám napojenú v počítači, kde mám CRM prihlásené na admin – tam mám
 * štítkovačku Brother". Počítač, na ktorom je stanica zapnutá, sa každých
 * pár sekúnd pozrie do tlačovej fronty a štítky vytlačí cez prehliadač.
 *
 * Zapnutie je na **tomto počítači** (localStorage), nie na konte: ten istý
 * admin môže mať CRM otvorené aj v notebooku bez štítkovačky.
 */
const KLUC = 'tlacova_stanica_949';
export const jeStanica = () => { try { return localStorage.getItem(KLUC) === '1'; } catch { return false; } };
export const nastavStanicu = (ano) => { try { if (ano) localStorage.setItem(KLUC, '1'); else localStorage.removeItem(KLUC); } catch { /* nič */ } };

/** Beží na pozadí celého CRM – mountuje sa v App. */
export function TlacovaStanicaBeh({ user }) {
  const bezi = useRef(false);
  useEffect(() => {
    if (!user || !['admin', 'user'].includes(user.role)) return undefined;
    let koniec = false;
    const krok = async () => {
      if (koniec || bezi.current || !jeStanica()) return;
      bezi.current = true;
      try {
        const { ulohy = [] } = await api.get(`/api/tlac/fronta?vezmi=1&stanica=${encodeURIComponent(user.name || '')}`);
        for (const u of ulohy) {
          const html = await api.get(`/api/tlac/${u.id}/html`);
          tlacHtml(typeof html === 'string' ? html : String(html));
          await api.post(`/api/tlac/${u.id}/hotovo`, {});
          /* dve tlače naraz by si prekážali v rámci */
          await new Promise((r) => setTimeout(r, 2500));
        }
      } catch { /* sieť – skúsi sa o chvíľu */ }
      finally { bezi.current = false; }
    };
    const t = setInterval(krok, 4000);
    krok();
    return () => { koniec = true; clearInterval(t); };
  }, [user?.id]);
  return null;
}

/** Nastavenie a prehľad (Sklad → Skener). */
export default function TlacovaStanica() {
  const toast = useToast();
  const [zap, setZap] = useState(jeStanica());
  const [d, setD] = useState(null);
  const nacitaj = () => api.get('/api/tlac/fronta').then(setD).catch(() => {});
  useEffect(() => { nacitaj(); const t = setInterval(nacitaj, 5000); return () => clearInterval(t); }, []);
  const prepni = (v) => { nastavStanicu(v); setZap(v); toast.success(v ? 'Tento počítač tlačí štítky z telefónov.' : 'Tlačová stanica vypnutá.'); };
  const skuska = async () => {
    try {
      await api.post('/api/tlac/stitok', { kod: 'SKUSKA', nazov: 'Skúšobný štítok – tlačová stanica', kusov: 1 });
      toast.success(zap ? 'Skúšobný štítok je vo fronte – o pár sekúnd sa vytlačí.' : 'Štítok je vo fronte. Vytlačí ho počítač so zapnutou stanicou.');
      nacitaj();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="card-p space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800"><Printer size={18} /> Tlačová stanica štítkov</h2>
          <p className="mt-0.5 max-w-2xl text-sm text-slate-500">
            V skeneri v telefóne: <b>Bez štítku</b> → nájdete kartu → počet → <b>Do súpisu a vytlačiť</b> (alebo „Vytlačiť štítky" pri riadku).
            Štítky 62 × 29 mm s čiarovým kódom vytlačí počítač, na ktorom je táto stanica zapnutá a CRM je otvorené.
          </p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm font-medium ring-1 ring-slate-200">
          <input type="checkbox" className="h-4 w-4" checked={zap} onChange={(e) => prepni(e.target.checked)} />
          Tento počítač tlačí štítky
        </label>
      </div>
      {zap && (
        <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900 ring-1 ring-amber-200">
          Aby sa tlačilo bez potvrdzovania, spustite na tomto počítači Chrome so štítkovačkou Brother ako predvolenou tlačiarňou
          a s prepínačom <code>--kiosk-printing</code> (návod v správe k verzii). Inak sa pri každom štítku ukáže okno tlače.
          Páska DK-22205 62 mm, dĺžka štítku 29 mm.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-secondary" onClick={skuska}><Tag size={15} /> Skúšobný štítok</button>
        {d && <span className="text-sm text-slate-500">Vo fronte čaká: <b>{d.cakajuce}</b></span>}
      </div>
      {!!d?.posledne?.length && (
        <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200">
          {d.posledne.slice(0, 8).map((u) => (
            <li key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
              <span className="font-mono text-xs">{u.kod}</span>
              <span className="min-w-0 flex-1 truncate text-slate-600">{u.nazov}</span>
              <span className="text-xs text-slate-500">{u.kusov} ks</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] ${u.stav === 'hotovo' ? 'bg-emerald-50 text-emerald-700' : u.stav === 'caka' ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                {u.stav === 'hotovo' ? 'vytlačené' : u.stav === 'caka' ? 'čaká' : u.stav === 'tlaci' ? 'tlačí sa' : u.stav}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
